/**
 * Admin LLM Usage Page
 *
 * Shows LiteLLM spend logs and usage metrics:
 * - Date range filter (7d/30d)
 * - Summary cards: Total Spend, Total Requests, Top Model
 * - Table of spend logs: timestamp, model, tokens (in/out), cost, user
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../platform/api';
import { dedupeRequest } from '../../lib/request-cache';
import {
  ArrowLeft,
  Activity,
  DollarSign,
  Hash,
  Cpu,
  AlertTriangle,
} from 'lucide-react';

interface SpendLog {
  request_id: string;
  call_type: string;
  api_key: string;
  spend: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  startTime: string;
  endTime: string;
  model: string;
  user: string;
  metadata: Record<string, unknown>;
  cache_hit: string;
  cache_key: string;
  request_tags: Record<string, unknown>;
}

type DateRange = '7d' | '30d';

export function AdminPerformancePage() {
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [logs, setLogs] = useState<SpendLog[]>([]);
  const fetchCountRef = useRef(0);

  useEffect(() => {
    const fetchId = ++fetchCountRef.current;
    let cancelled = false;

    const fetchData = async () => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
      setNotConfigured(false);

      try {
        const now = new Date();
        const daysAgo = dateRange === '7d' ? 7 : 30;
        const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

        // Fetch individual request logs (no date params — LiteLLM aggregates with date filters)
        const cacheKey = `admin-litellm-spend-${dateRange}`;
        const result = await dedupeRequest(cacheKey, () =>
          apiRequest<{ data: SpendLog[] }>('/api/admin/litellm/spend/logs')
        );

        if (!cancelled && fetchId === fetchCountRef.current) {
          const data = (result as any).data || result;
          const allLogs = Array.isArray(data) ? data : [];
          // Filter client-side by date range
          const filtered = allLogs.filter((log) => {
            const logDate = new Date(log.startTime);
            return logDate >= startDate && logDate <= now;
          });
          setLogs(filtered);
        }
      } catch (err: any) {
        if (!cancelled && fetchId === fetchCountRef.current) {
          if (err?.message?.includes('503') || err?.message?.includes('NOT_CONFIGURED')) {
            setNotConfigured(true);
          } else {
            setError(err instanceof Error ? err.message : 'Failed to load LLM usage data');
          }
        }
      } finally {
        if (!cancelled && fetchId === fetchCountRef.current) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [dateRange]);

  const summary = useMemo(() => {
    const totalSpend = logs.reduce((sum, log) => sum + (log.spend || 0), 0);
    const totalRequests = logs.length;

    const modelCounts = new Map<string, number>();
    logs.forEach((log) => {
      modelCounts.set(log.model, (modelCounts.get(log.model) || 0) + 1);
    });
    const topModel = modelCounts.size > 0
      ? Array.from(modelCounts.entries()).sort((a, b) => b[1] - a[1])[0][0]
      : null;

    return { totalSpend, totalRequests, topModel };
  }, [logs]);

  const formatCost = (amount: number) => `$${amount.toFixed(4)}`;
  const formatNumber = (num: number) => num.toLocaleString();
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (notConfigured) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">LLM Usage</h1>
                <p className="mt-1 text-sm text-gray-500">Monitor LLM usage via LiteLLM proxy</p>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">LiteLLM Not Configured</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
              Set <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">LITELLM_PROXY_URL</code> and{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">LITELLM_API_KEY</code> environment
              variables to enable LLM usage tracking.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">LLM Usage</h1>
              <p className="mt-1 text-sm text-gray-500">Monitor LLM usage via LiteLLM proxy</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Date Range Filter */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Date Range:</span>
            <div className="flex gap-2">
              {(['7d', '30d'] as DateRange[]).map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    dateRange === range
                      ? 'bg-mazo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {range === '7d' ? 'Last 7 days' : 'Last 30 days'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-mazo-600" />
            <p className="mt-4 text-gray-500">Loading LLM usage data...</p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Spend</p>
                    <p className="mt-2 text-3xl font-bold text-gray-900">
                      {formatCost(summary.totalSpend)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-green-100">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Requests</p>
                    <p className="mt-2 text-3xl font-bold text-gray-900">
                      {formatNumber(summary.totalRequests)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-mazo-100">
                    <Hash className="w-6 h-6 text-mazo-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Top Model</p>
                    <p className="mt-2 text-lg font-bold text-gray-900 truncate">
                      {summary.topModel || 'N/A'}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-100">
                    <Cpu className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Spend Logs Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Spend Logs</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {logs.length} request{logs.length !== 1 ? 's' : ''} found
                </p>
              </div>

              {logs.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">No LLM requests found for this period</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Timestamp
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Model
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Input Tokens
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Output Tokens
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          User
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {logs.map((log, idx) => (
                        <tr key={log.request_id || idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(log.startTime)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                              {log.model}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatNumber(log.prompt_tokens || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatNumber(log.completion_tokens || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCost(log.spend || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {log.user || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminPerformancePage;
